import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { ACTIONS, writeLog } from "./auditService";
import { auth, db } from "./firebase";

const ADMIN_ACCESS_CODE = process.env.REACT_APP_ADMIN_CODE;

function resolveRole(accessCode) {
  if (!accessCode || !ADMIN_ACCESS_CODE) return "standard";
  return accessCode.trim() === ADMIN_ACCESS_CODE.trim() ? "admin" : "standard";
}

function normalizeRole(role) {
  if (typeof role !== "string") return "";
  return role.trim().toLowerCase();
}

export async function signUp(email, password, displayName, accessCode = "") {
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  const user = credential.user;
  await updateProfile(user, { displayName });
  const role = resolveRole(accessCode);

  await setDoc(doc(db, "users", user.uid), {
    email,
    displayName,
    role,
    createdAt: serverTimestamp(),
    lastLogin: serverTimestamp(),
  });

  await writeLog({ uid: user.uid, email, displayName }, ACTIONS.USER_SIGNUP, "user", user.uid, `User: ${displayName}`, { role });
  return { user, role };
}

export async function login(email, password) {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  const user = credential.user;
  const userRef = doc(db, "users", user.uid);
  const existing = await getDoc(userRef);
  if (existing.exists()) {
    await updateDoc(userRef, { lastLogin: serverTimestamp() });
  } else {
    await setDoc(
      userRef,
      {
        email: user.email || email,
        displayName: user.displayName || "",
        role: "standard",
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
      },
      { merge: true }
    );
  }
  const profile = await getUserProfile(user.uid);
  if (profile?.role && normalizeRole(profile.role) === "admin" && profile.role !== "admin") {
    await updateDoc(userRef, { role: "admin" });
  }
  const normalizedProfile = await getUserProfile(user.uid);
  await writeLog(
    { uid: user.uid, email: normalizedProfile?.email || user.email, displayName: normalizedProfile?.displayName || user.displayName },
    ACTIONS.USER_LOGIN,
    "user",
    user.uid,
    `User: ${normalizedProfile?.displayName || user.displayName || user.email}`
  );
  return { user, profile: normalizedProfile };
}

export async function logout() {
  await signOut(auth);
}

export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? { uid: snap.id, ...snap.data() } : null;
}
